/**
 * What a viewer may see where a mnemonic would render.
 *
 * - `own` — the viewer's own saved mnemonic. Always shown; it is their content.
 * - `visible` — a catalog mnemonic the viewer is entitled to read, already revealed.
 * - `revealable` — a FREE catalog mnemonic hidden behind a reveal button. Signed-out viewers on
 *   the Reference page only: the text is present on the device, the button is a deliberate speed
 *   bump that creates the moment to pitch an account.
 * - `premium-locked` — a PREMIUM catalog mnemonic the viewer cannot read. No reveal button,
 *   because there is nothing to reveal: the text is not in the client's database at all.
 * - `absent` — no mnemonic exists for this grapheme yet.
 */
export type MnemonicGateState = "absent" | "own" | "premium-locked" | "revealable" | "visible";

export type ResolveMnemonicGateStateArgs = {
  /** Whether a catalog mnemonic exists for this grapheme (independent of readability). */
  hasCatalogMnemonic: boolean;
  /** Whether the viewer has written their own mnemonic. */
  hasOwnMnemonic: boolean;
  /** Whether the viewer already tapped reveal for this mnemonic. */
  hasRevealed: boolean;
  /** Whether this grapheme's mnemonic is premium (outside the free level range). */
  isDescriptionPremium: boolean;
  isPremium: boolean;
  /**
   * Whether this renders on the standalone Reference page — the web-only, direct-link SEO surface.
   * The reveal step exists solely for that page's search visitors, so every other surface (the
   * Info and Mnemonics panels, which are the only views reachable at all on native) skips it.
   */
  isReferenceSurface: boolean;
  isSignedIn: boolean;
};

/**
 * Decide what to render where a mnemonic would go.
 *
 * Note the asymmetry between the two locked states, which mirrors where the text physically is:
 * a free mnemonic is in the client database, so hiding it is a UI choice and revealing it is
 * instant. A premium mnemonic is stripped from that database entirely, so there is no reveal
 * button to offer — only a prompt to sign in and unlock.
 */
export function resolveMnemonicGateState({
  hasCatalogMnemonic,
  hasOwnMnemonic,
  hasRevealed,
  isDescriptionPremium,
  isPremium,
  isReferenceSurface,
  isSignedIn,
}: ResolveMnemonicGateStateArgs): MnemonicGateState {
  // A viewer's own mnemonic is never gated — they wrote it.
  if (hasOwnMnemonic) {
    return "own";
  }

  if (!hasCatalogMnemonic) {
    return "absent";
  }

  if (isDescriptionPremium && !isPremium) {
    return "premium-locked";
  }

  // Only the Reference page gates a free mnemonic, and only for guests. Inside the app proper the
  // reveal button is pure friction on content the reader is already entitled to.
  if (isReferenceSurface && !isSignedIn && !hasRevealed) {
    return "revealable";
  }

  return "visible";
}

/**
 * Whether to pitch an account under a mnemonic the viewer can currently read.
 *
 * This is the back half of the reveal moment, so it is bound to the same conditions as the reveal
 * button itself: it appears only where a reveal was actually offered — the Reference page, to a
 * signed-out reader who just tapped through. Showing it anywhere the button never appears would
 * nag readers mid-lesson about content they already have, with no act of revealing to give the
 * pitch its context.
 */
export function shouldShowRevealedMnemonicUpsell({
  gateState,
  isReferenceSurface,
  isSignedIn,
}: {
  gateState: MnemonicGateState;
  isReferenceSurface: boolean;
  isSignedIn: boolean;
}): boolean {
  return gateState === "visible" && isReferenceSurface && !isSignedIn;
}
